/**
 * 工作流模板管理命令
 */

import { Command } from 'commander';
import { TemplateManager, TemplateCategory, TemplateContext } from '../core/template-system';
import { logger } from '../utils/logger';
import chalk from 'chalk';
import Table from 'cli-table3';
import inquirer from 'inquirer';
import * as path from 'path';

export function createWorkflowTemplateCommand(workflowDir: string): Command {
  const templateManager = new TemplateManager(workflowDir);
  
  const command = new Command('workflow-template')
    .description('管理和使用工作流模板（完整项目模板）')
    .alias('wf-tpl');
  
  // 列出模板
  command
    .command('list')
    .alias('ls')
    .description('列出所有可用的工作流模板')
    .option('-c, --category <category>', '按分类筛选')
    .action(async (options) => {
      await templateManager.initialize();
      
      const templates = templateManager.getTemplates(options.category);
      
      if (templates.length === 0) {
        console.log(chalk.yellow('没有找到模板'));
        return;
      }
      
      // 按分类分组
      const grouped = templates.reduce((acc, template) => {
        const category = template.metadata.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(template);
        return acc;
      }, {} as Record<string, typeof templates>);
      
      // 显示分类
      Object.entries(grouped).forEach(([category, categoryTemplates]) => {
        console.log(chalk.bold.blue(`\n${getCategoryName(category as TemplateCategory)}:`));
        
        const table = new Table({
          head: ['ID', '名称', '难度', '预计时间', '标签'],
          colWidths: [20, 30, 12, 15, 30]
        });
        
        categoryTemplates.forEach(template => {
          table.push([
            template.metadata.id,
            template.metadata.name,
            getDifficultyLabel(template.metadata.difficulty),
            template.metadata.estimatedTime || 'N/A',
            (template.metadata.tags || []).join(', ')
          ]);
        });
        
        console.log(table.toString());
      });
    });
  
  // 查看模板详情
  command
    .command('info <templateId>')
    .description('查看模板详细信息')
    .action(async (templateId) => {
      await templateManager.initialize();
      
      const template = templateManager.getTemplate(templateId);
      if (!template) {
        console.error(chalk.red(`模板未找到: ${templateId}`));
        return;
      }
      
      console.log(chalk.bold.blue('模板信息:'));
      console.log(`ID: ${template.metadata.id}`);
      console.log(`名称: ${template.metadata.name}`);
      console.log(`描述: ${template.metadata.description}`);
      console.log(`版本: ${template.metadata.version}`);
      console.log(`作者: ${template.metadata.author || '未知'}`);
      console.log(`分类: ${getCategoryName(template.metadata.category)}`);
      console.log(`难度: ${getDifficultyLabel(template.metadata.difficulty)}`);
      console.log(`预计时间: ${template.metadata.estimatedTime || 'N/A'}`);
      
      if (template.metadata.tags && template.metadata.tags.length > 0) {
        console.log(`标签: ${template.metadata.tags.join(', ')}`);
      }
      
      if (template.metadata.prerequisites && template.metadata.prerequisites.length > 0) {
        console.log('\n前置要求:');
        template.metadata.prerequisites.forEach(p => console.log(`  - ${p}`));
      }
      
      if (template.metadata.outcomes && template.metadata.outcomes.length > 0) {
        console.log('\n预期成果:');
        template.metadata.outcomes.forEach(o => console.log(`  - ${o}`));
      }
      
      if (template.workflow.phases) {
        console.log(`\n工作流阶段 (${template.workflow.phases.length} 个):`);
        template.workflow.phases.forEach(phase => {
          console.log(`  - ${phase.name} (${phase.tasks.length} 个任务)`);
        });
      }
      
      if (template.workflow.variables) {
        console.log('\n可配置变量:');
        template.workflow.variables.forEach(v => {
          console.log(`  - ${v.name} (${v.type}): ${v.description}`);
        });
      }
    });
  
  // 应用模板
  command
    .command('apply <templateId>')
    .alias('use')
    .description('应用模板创建新项目')
    .option('-p, --path <path>', '项目路径', '.')
    .option('-n, --name <name>', '项目名称')
    .option('--skip-docs', '跳过文档创建')
    .option('--skip-configs', '跳过配置文件')
    .option('--no-interactive', '非交互模式')
    .action(async (templateId, options) => {
      await templateManager.initialize();
      
      const template = templateManager.getTemplate(templateId);
      if (!template) {
        console.error(chalk.red(`模板未找到: ${templateId}`));
        return;
      }
      
      console.log(chalk.blue(`使用模板: ${template.metadata.name}`));
      
      // 收集变量值
      const variables: Record<string, any> = {};
      
      if (template.workflow.variables && options.interactive !== false) {
        console.log(chalk.yellow('\n请提供以下配置:'));
        
        for (const varDef of template.workflow.variables) {
          const answer = await inquirer.prompt([{
            type: varDef.type === 'array' ? 'checkbox' : 
                  varDef.choices ? 'list' : 'input',
            name: 'value',
            message: `${varDef.description}:`,
            default: varDef.default,
            choices: varDef.choices,
            validate: (input) => {
              if (varDef.required && !input) {
                return '此项为必填';
              }
              if (varDef.validation) {
                const regex = new RegExp(varDef.validation);
                if (!regex.test(String(input))) {
                  return '格式不正确';
                }
              }
              return true;
            }
          }]);
          
          variables[varDef.name] = answer.value;
        }
      } else if (template.workflow.variables) {
        // 使用默认值
        template.workflow.variables.forEach(v => {
          variables[v.name] = v.default || '';
        });
      }
      
      // 如果提供了项目名称，覆盖变量
      if (options.name) {
        variables.projectName = options.name;
      }
      
      const projectPath = path.resolve(options.path);
      
      try {
        console.log(chalk.blue('\n正在应用模板...'));
        
        const context: TemplateContext = {
          projectName: variables.projectName || path.basename(projectPath),
          projectPath,
          variables,
          options: {
            skipDocs: options.skipDocs,
            skipConfigs: options.skipConfigs
          }
        };
        
        const result = await templateManager.applyTemplate(templateId, context);
        
        console.log(chalk.green('\n✓ 模板应用成功!'));
        console.log(`项目路径: ${result.projectPath}`);
        console.log(`创建任务: ${result.tasks.length} 个`);
        console.log(`创建文档: ${result.documents.length} 个`);
        console.log(`创建配置: ${result.configs.length} 个`);
        
        if (result.errors.length > 0) {
          console.log(chalk.yellow('\n警告:'));
          result.errors.forEach(err => console.log(`  - ${err}`));
        }
        
        console.log(chalk.cyan('\n下一步:'));
        console.log(`1. cd ${path.relative(process.cwd(), projectPath)}`);
        console.log('2. workflow init');
        console.log('3. workflow next');
      } catch (error) {
        console.error(chalk.red(`应用模板失败: ${error.message}`));
      }
    });
  
  // 搜索模板
  command
    .command('search <query>')
    .description('搜索模板')
    .action(async (query) => {
      await templateManager.initialize();
      
      const results = templateManager.searchTemplates(query);
      
      if (results.length === 0) {
        console.log(chalk.yellow('没有找到匹配的模板'));
        return;
      }
      
      console.log(chalk.blue(`找到 ${results.length} 个模板:\n`));
      
      const table = new Table({
        head: ['ID', '名称', '分类', '描述'],
        colWidths: [20, 25, 20, 50]
      });
      
      results.forEach(template => {
        table.push([
          template.metadata.id,
          template.metadata.name,
          getCategoryName(template.metadata.category),
          template.metadata.description.slice(0, 47) + '...'
        ]);
      });
      
      console.log(table.toString());
    });
  
  return command;
}

// 获取分类名称
function getCategoryName(category: TemplateCategory): string {
  const names: Record<TemplateCategory, string> = {
    'web-development': 'Web 开发',
    'mobile-development': '移动开发',
    'backend-development': '后端开发',
    'devops': 'DevOps',
    'data-science': '数据科学',
    'machine-learning': '机器学习',
    'documentation': '文档编写',
    'testing': '测试',
    'project-management': '项目管理',
    'open-source': '开源项目',
    'startup': '创业项目',
    'education': '教育培训'
  };
  return names[category] || category;
}

// 获取难度标签
function getDifficultyLabel(difficulty?: string): string {
  const labels = {
    'beginner': chalk.green('初级'),
    'intermediate': chalk.yellow('中级'),
    'advanced': chalk.red('高级')
  };
  return labels[difficulty as keyof typeof labels] || '未知';
}